import {
  BookOpen,
  Calendar,
  Download,
  FileCheck,
  FileText,
  MoreHorizontal,
  Pencil,
  Send,
  Trash2,
} from 'lucide-react';
import { Menu } from '@/components/ui/Menu';
import { cn } from '@/lib/utils';
import type { HomeworkItem } from '@/types/homework';
import { formatFileSize } from '../../activities/utils';
import { formatInstituteDate, homeworkState, isHomeworkEditable } from '../../utils';

const ROW_ICONS = [
  { icon: BookOpen, bg: 'bg-purple-500/15 text-purple-400 border border-purple-500/25' },
  { icon: Pencil, bg: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25' },
  { icon: FileText, bg: 'bg-blue-500/15 text-blue-400 border border-blue-500/25' },
  { icon: BookOpen, bg: 'bg-amber-500/15 text-amber-400 border border-amber-500/25' },
];

interface HomeworkTableProps {
  homework: HomeworkItem[];
  today: string;
  defaultSubject: string;
  editMenuLabel: string;
  onReview: (item: HomeworkItem) => void;
  onStartEditing: (item: HomeworkItem) => void;
  onPublishDraft: (item: HomeworkItem) => void;
  onDownloadWorksheet: (item: HomeworkItem) => void;
  onRemove: (item: HomeworkItem) => void;
}

export function HomeworkTable({
  homework,
  today,
  defaultSubject,
  editMenuLabel,
  onReview,
  onStartEditing,
  onPublishDraft,
  onDownloadWorksheet,
  onRemove,
}: HomeworkTableProps) {
  return (
    <div className="card bg-surface border border-line rounded-[16px] overflow-hidden shadow-2xs">
      <div className="relative overflow-x-auto table-scrollbar">
        <table className="w-full text-left border-collapse min-w-[760px]">
          <thead>
            <tr className="bg-surface text-[11px] font-bold text-ink-3 uppercase tracking-wider border-b border-line">
              <th className="py-3.5 px-4 sm:px-5">Homework</th>
              <th className="py-3.5 px-3 min-w-[110px] text-center">Subject</th>
              <th className="py-3.5 px-3 min-w-[125px]">Due Date</th>
              <th className="py-3.5 px-3 min-w-[120px] text-center">Submissions</th>
              <th className="py-3.5 px-3 min-w-[100px] text-center">Status</th>
              <th className="py-3.5 px-4 w-[60px] text-right">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {homework.map((item, idx) => {
              const state = homeworkState(item);
              const isWithdrawn = item.withdrawnAt !== null;
              const formattedDueDate = new Date(`${item.dueDate}T00:00:00Z`).toLocaleDateString(
                'en-GB',
                {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'UTC',
                },
              );

              const styleObj = ROW_ICONS[idx % ROW_ICONS.length];
              const IconComponent = styleObj.icon;

              const { submissionCount: submitted, totalStudents } = item;
              const submissionPercent =
                totalStudents > 0 ? Math.round((submitted / totalStudents) * 100) : 0;

              return (
                <tr
                  key={item.uuid}
                  className="group hover:bg-slate-50/80 dark:hover:bg-surface-2 transition-colors"
                >
                  {/* 1. Homework Title, Description & Worksheet */}
                  <td className="py-4 px-4 sm:px-5 align-middle max-w-[340px]">
                    <div className="flex items-start gap-3 min-w-0">
                      <span
                        className={cn(
                          'grid h-9 w-9 shrink-0 place-items-center rounded-[10px] shadow-2xs mt-0.5',
                          styleObj.bg,
                        )}
                      >
                        <IconComponent className="h-4 w-4" />
                      </span>

                      <div className="min-w-0 flex-1">
                        {isWithdrawn ? (
                          <h3 className="text-sm font-bold text-ink line-clamp-1">{item.title}</h3>
                        ) : (
                          <h3
                            className="text-sm font-bold text-ink hover:text-indigo-400 cursor-pointer transition-colors line-clamp-1"
                            onClick={() =>
                              state === 'draft' ? onStartEditing(item) : onReview(item)
                            }
                            title={item.title}
                          >
                            {item.title}
                          </h3>
                        )}

                        <p className="text-xs text-ink-3 font-medium mt-0.5">
                          Set {formatInstituteDate(item.assignedDate)}
                          {item.withdrawnAt !== null &&
                            ` · Withdrawn ${formatInstituteDate(item.withdrawnAt)}`}
                          {item.maxMarks !== null && ` · Max marks ${item.maxMarks}`}
                        </p>

                        {item.instructions && (
                          <p className="text-xs text-ink-2 font-normal mt-1 line-clamp-1">
                            {item.instructions}
                          </p>
                        )}

                        {item.attachment && !isWithdrawn && (
                          <button
                            type="button"
                            onClick={() => void onDownloadWorksheet(item)}
                            className="inline-flex items-center gap-1.5 mt-1.5 rounded-field border border-line bg-surface-2 px-2 py-0.5 text-left text-2xs text-ink-2 font-medium transition-colors hover:border-indigo-500/50 hover:text-indigo-400"
                            title="Download worksheet"
                          >
                            <Download className="h-3 w-3 shrink-0 text-indigo-400" />
                            <span className="truncate max-w-[150px]">
                              {item.attachment.fileName}
                            </span>
                            {item.attachment.sizeBytes !== null && (
                              <span className="text-ink-3 text-2xs font-medium">
                                ({formatFileSize(item.attachment.sizeBytes)})
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* 2. Subject Badge */}
                  <td className="py-4 px-3 text-center align-middle whitespace-nowrap">
                    <span className="px-2.5 py-1 rounded-pill bg-indigo-500/10 text-indigo-400 border border-indigo-500/25 text-xs font-bold inline-block">
                      {item.subject?.name || defaultSubject}
                    </span>
                  </td>

                  {/* 3. Due Date */}
                  <td className="py-4 px-3 align-middle whitespace-nowrap">
                    <p className="text-xs font-bold flex items-center gap-1.5 text-emerald-400">
                      <Calendar className="h-3.5 w-3.5 shrink-0" />
                      <span>{formattedDueDate}</span>
                    </p>
                  </td>

                  {/* 4. Submissions */}
                  <td className="py-4 px-3 text-center align-middle">
                    <div className="flex flex-col items-center justify-center">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-ink">
                          {submitted}/{totalStudents}
                        </span>
                        <span className="text-xs text-ink-3 font-semibold">
                          {submissionPercent}%
                        </span>
                      </div>
                      <div className="h-1.5 w-20 rounded-full bg-surface-3 overflow-hidden mt-1.5">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            submissionPercent === 100
                              ? 'bg-emerald-500'
                              : submissionPercent > 0
                                ? 'bg-teal-400'
                                : 'bg-transparent',
                          )}
                          style={{ width: `${submissionPercent}%` }}
                        />
                      </div>
                    </div>
                  </td>

                  {/* 5. Status Pill */}
                  <td className="py-4 px-3 text-center align-middle whitespace-nowrap">
                    <span
                      className={cn(
                        'px-2.5 py-0.5 rounded-pill text-xs font-bold border inline-block text-center whitespace-nowrap',
                        isWithdrawn
                          ? 'bg-surface-2 text-ink-3 border-line'
                          : state === 'draft'
                            ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                            : state === 'completed'
                              ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                              : 'bg-blue-500/15 text-blue-400 border-blue-500/30',
                      )}
                    >
                      {isWithdrawn
                        ? 'Withdrawn'
                        : state === 'draft'
                          ? 'Draft'
                          : state === 'completed'
                            ? 'Completed'
                            : 'Open'}
                    </span>
                  </td>

                  {/* 6. Actions Menu */}
                  <td className="py-4 px-4 text-right align-middle">
                    {!isWithdrawn && (
                      <Menu
                        align="end"
                        label={`Actions for ${item.title}`}
                        triggerClassName="grid h-8 w-8 ml-auto place-items-center rounded-full text-ink-3 hover:text-ink hover:bg-surface-2 transition-colors cursor-pointer border-0 bg-transparent shadow-none"
                        trigger={<MoreHorizontal className="h-4 w-4" aria-hidden />}
                        items={[
                          state === 'draft'
                            ? {
                                key: 'publish',
                                label: 'Publish',
                                icon: <Send className="h-3.5 w-3.5 text-indigo-400" />,
                                onSelect: () => onPublishDraft(item),
                              }
                            : {
                                key: 'review',
                                label: 'Review Submissions',
                                icon: <FileCheck className="h-3.5 w-3.5 text-indigo-400" />,
                                onSelect: () => onReview(item),
                              },
                          ...(isHomeworkEditable(item, today)
                            ? [
                                {
                                  key: 'edit',
                                  label: editMenuLabel,
                                  icon: <Pencil className="h-3.5 w-3.5 text-emerald-400" />,
                                  onSelect: () => onStartEditing(item),
                                },
                              ]
                            : []),
                          ...(item.attachment
                            ? [
                                {
                                  key: 'download',
                                  label: 'Download Worksheet',
                                  icon: <Download className="h-3.5 w-3.5 text-sky-400" />,
                                  onSelect: () => void onDownloadWorksheet(item),
                                },
                              ]
                            : []),
                          // Past its due date the work is a closed record —
                          // the server refuses a withdrawal, as it does an edit.
                          ...(isHomeworkEditable(item, today)
                            ? [
                                {
                                  key: 'delete',
                                  label: state === 'draft' ? 'Delete Draft' : 'Withdraw Homework',
                                  icon: <Trash2 className="h-3.5 w-3.5 text-rose-500" />,
                                  onSelect: () => onRemove(item),
                                },
                              ]
                            : []),
                        ]}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
